/* eslint-disable camelcase */

export enum PRODUCT_TYPES {
  SINGLE_PRODUCT = 'SingleProduct',
  PACK = 'Pack',
}

interface ActiveSubstance {
  name: string
  unit: string
  value: number
}

export interface ProductCategory {
  code: string
  label: string
}

export type ProductCategories = ProductCategory[][]

interface Toxicity {
  id: string
  name: string
}

interface UsageByCrop {
  crop_id: string
  crop_name: string
}

interface AssociatedProduct {
  _id: string
  categories: ProductCategories
  imageUrl: string
  name: string
  supplier: string
  kind: string
  sellable: boolean
}

interface ProductVariant {
  /** @deprecated redundant with base info at Product level */
  articleReferenceSupplier?: string
  assortable: boolean
  ballType?: string[]
  capacity?: string
  color?: string
  flowRate?: number
  supplier?: string
  associatedSeedTreatmentManagement?: string
  diameter?: number
  ean?: string
  heightInM?: number
  id: string
  isSoldAsUnit: boolean
  knotResistance?: number
  length?: number
  lengthInM?: number
  lengthInMeterPerKilo?: number
  measure_quantity: number
  measure_unit: string
  name: string
  packaging?: string
  perforationResistance?: number
  precocity?: string
  productId: string
  productKind: string
  productUnit?: string
  sellable?: boolean
  surface?: number
  tearResistance?: number
  thicknessMm?: number
  sectionDiameter?: number
  height?: number
  meshSizeMm?: string
  totalLengthM?: number
  offline?: boolean
  type?: string
  productConditionning?: string
  /** Weight in kilograms */
  weight?: number
  weightInGrammPerSquareMeter?: number
  width?: number
  widthUnit?: string
}

interface BaseProduct {
  _id: string
  associatedProducts?: AssociatedProduct[]
  isMentionBioControl?: boolean
  categories: ProductCategories
  characteristics?: string
  color?: string
  company?: string
  compatibility?: string[]
  contributionMode?: string
  cooperative_product_code?: string
  cropName?: string
  description?: string
  valuation?: string[]
  energyType?: string[]
  productType?: string
  family: string
  generation?: string
  images: string[]
  isHighlighted?: boolean
  isCooperativeProduct?: boolean
  isRestrictedExplosivesPrecursor: boolean
  isSupplierHighlighted?: boolean
  kind: string
  material?: string
  compositionMix?: string[]
  mixed?: boolean
  name: string
  isMentionOrganic?: boolean
  origin?: string
  productManualUrls?: string[]
  productSheetUrls?: string[]
  profile?: string
  obtenteur?: string
  resistance?: string
  engraisPackagingForm?: string
  specificity?: string
  standard?: string
  stimulatingSubstance?: string
  supplier?: string
  supplierLogo?: string
  supplierUniversePageLink?: string
  supplierScore?: number
  targets?: {
    id: string
    name: string
  }[]
  type?: PRODUCT_TYPES
  variants: ProductVariant[]
  version?: string
}

interface SingleProductAttributes {
  active_substances?: ActiveSubstance[]
  cropProtectionProductFormulationName?: string
  market_authorization?: string
  safetySheetUrls?: string[]
  toxicities?: Toxicity[]
  associatedUses?: UsageByCrop[]
}

interface FlattenedAttribute<T> {
  product_name: string
  value: T
}

export interface FlattenedPackProductAttributes {
  active_substances: FlattenedAttribute<ActiveSubstance[]>[]
  safetySheetUrls: FlattenedAttribute<string[]>[]
  cropProtectionProductFormulationName: FlattenedAttribute<string>[]
  market_authorization: FlattenedAttribute<string>[]
  toxicities: FlattenedAttribute<Toxicity[]>[]
  associatedUses: {
    id: number
    product_name: string
    value: UsageByCrop[]
  }[]
}

interface CooperativeVariantFields {
  associated?: boolean
  cooperativeVariantId?: number
  isSoldAsUnit: boolean
  published?: boolean
  stockManagementType?: 'managed' | 'notManaged'
  stockQuantity?: number
  taxes?: {
    rpd: number
    rpdUnit: string
    tva: number
  }
}

export type CooperativeVariant = ProductVariant & CooperativeVariantFields

interface CooperativeProductFields {
  cooperativeReview?: {
    tips?: string
    upsides?: string
  }
  variants: CooperativeVariant[]
  associatedProducts?: AssociatedProduct[]
}

type SingleProduct = BaseProduct & SingleProductAttributes

export type FlattenedPackProduct = BaseProduct & FlattenedPackProductAttributes
export type Product = SingleProduct | FlattenedPackProduct

export type CooperativeProduct = Omit<Product, 'variants'> & CooperativeProductFields
